import { flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import moment from "moment";
import { useMemo, useState } from "react";
import { Button, Table } from "react-bootstrap";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import { Link } from "react-router-dom";
import searchHistory from "../../Helpers/API/HistoryApi";
import HistoryModel from "./HistoryModel";
import HistoryPagination from "./HistoryPagination";
import HistorySearch from "./HistorySearch";

const HistoryList = () => {
	const [history, setHistory] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [selectedRow, setSelectedRow] = useState();
	const [noRecordsFound, setNoRecordsFound] = useState(false);

	const handleClose = () => setShowModal(false);

	const handleShow = (row) => {
		setSelectedRow(row);
		setShowModal(true);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: surpressed as fix generated another error
	const columns = useMemo(
		() => [
			{
				header: "First Name",
				accessorKey: "firstName",
			},
			{
				header: "Last Name",
				accessorKey: "lastName",
			},
			{
				header: "Email Address",
				accessorKey: "emailAddress",
				meta: {
					className: "d-none d-sm-table-cell",
				},
			},
			{
				header: "Course Code",
				accessorKey: "activityCode",
				meta: {
					className: "d-none d-sm-table-cell",
				},
			},
			{
				header: "Instance Code",
				accessorKey: "instanceCode",
				meta: {
					className: "d-none d-sm-table-cell",
				},
			},
			{
				header: "Course Start Date",
				accessorKey: "courseStartDate",
				meta: {
					className: "d-none d-sm-table-cell",
				},
				cell: ({ getValue }) => moment(getValue()).format("DD/MM/yyyy"),
			},
			{
				header: "Survey",
				accessorKey: "surveyName",
				meta: {
					className: "d-none d-sm-table-cell",
				},
			},
			{
				header: "Send On",
				accessorKey: "sendSurveyOn",
				meta: {
					className: "d-none d-sm-table-cell",
				},
				cell: ({ getValue }) => moment(getValue()).format("DD/MM/yyyy"),
			},
			{
				header: "Is Sent?",
				accessorKey: "isSurveySent",
				meta: {
					className: "d-none d-sm-table-cell",
				},
				cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
			},
			{
				header: "Response Received?",
				accessorKey: "isResponseReceived",
				meta: {
					className: "d-none d-sm-table-cell",
				},
				cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
			},
			{
				header: "",
				id: "action-link",
				enableSorting: false,
				cell: ({ row }) => (
					<Button variant="primary" size="sm" onClick={() => handleShow(row.original)}>
						View Details
					</Button>
				),
			},
		],
		[],
	);

	const [sorting, setSorting] = useState([
		{
			id: "sendSurveyOn",
			desc: false,
		},
	]);

	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: 20,
	});

	const table = useReactTable({
		columns,
		data: history,
		state: {
			sorting,
			pagination,
		},
		onSortingChange: setSorting,
		onPaginationChange: setPagination,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
	});

	const headerGroups = table.getHeaderGroups();
	const rows = table.getRowModel().rows;
	const pageOptions = table.getPageOptions();
	const pageCount = table.getPageCount();
	const canNextPage = table.getCanNextPage();
	const canPreviousPage = table.getCanPreviousPage();
	const gotoPage = (page) => table.setPageIndex(page);
	const nextPage = () => table.nextPage();
	const previousPage = () => table.previousPage();
	const { pageIndex } = table.getState().pagination;

	const showSortIcon = (column) => {
		const sortDirection = column.getIsSorted();

		if (sortDirection) {
			if (sortDirection === "desc") {
				return " 🔽";
			}

			return " 🔼";
		}

		return "";
	};

	const initialSearchValues = {
		firstName: "",
		lastName: "",
		emailAddress: "",
		activityCode: "",
		instanceCode: "",
		courseStartDate: "",
		sendOn: "",
		survey: "",
		adhocSurvey: "",
	};

	const onSubmit = async (searchCriteria) => {
		const data = await searchHistory(
			searchCriteria.firstName,
			searchCriteria.lastName,
			searchCriteria.emailAddress,
			searchCriteria.activityCode,
			searchCriteria.instanceCode,
			searchCriteria.courseStartDate,
			searchCriteria.sendOn,
			searchCriteria.isSent,
			"",
			searchCriteria.survey,
			searchCriteria.adhocSurvey,
		);
		setHistory(data);
		setNoRecordsFound(data.length === 0);
	};

	const sentCount = useMemo(() => history.filter((record) => record.isSurveySent === true).length, [history]);
	const responseReceivedCount = useMemo(() => history.filter((record) => record.isResponseReceived === true).length, [history]);

	return (
		<div>
			<div className="breadcrumbs">
				<Breadcrumb>
					<Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
						Home
					</Breadcrumb.Item>
					<Breadcrumb.Item active>Engagement History</Breadcrumb.Item>
				</Breadcrumb>
			</div>
			<h4>Engagement History</h4>
			<div className="FormWrapper history-search">
				<HistorySearch onSubmit={onSubmit} initialValues={initialSearchValues} />
			</div>
			{history.length > 0 && (
				<div className="result-count">
					<div>
						<strong className="result-label">Surveys sent:</strong>
						{sentCount}
					</div>
					<div>
						<strong className="result-label">Responses received:</strong>
						{responseReceivedCount}
					</div>
				</div>
			)}
			<div className="table-wrapper" style={{ clear: "both" }}>
				<HistoryModel show={showModal} handleClose={handleClose} history={selectedRow} />
				<Table striped bordered hover>
					<thead>
						{headerGroups.map((headerGroup) => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<th
										key={header.id}
										className={header.column.columnDef.meta?.className}
										onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
									>
										{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
										<span>{showSortIcon(header.column)}</span>
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody>
						{noRecordsFound ? (
							<tr>
								<td colSpan={columns.length}>
									<span className="d-block invalid-feedback">No engagement records were found</span>
								</td>
							</tr>
						) : (
							rows.map((row) => {
								return (
									<tr key={row.id}>
										{row.getVisibleCells().map((cell) => (
											<td key={cell.id} className={cell.column.columnDef.meta?.className}>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</td>
										))}
									</tr>
								);
							})
						)}
					</tbody>
				</Table>
				<HistoryPagination
					gotoPage={gotoPage}
					previousPage={previousPage}
					nextPage={nextPage}
					canPreviousPage={canPreviousPage}
					canNextPage={canNextPage}
					pageCount={pageCount}
					pageOptions={pageOptions}
					pageIndex={pageIndex}
				/>
			</div>
		</div>
	);
};

export default HistoryList;
